/**
 * ServicesProvider + hooks — makes {@link PortalServices}, the live auth
 * state, and the PROFILE DIRECTORY available to the component tree via React
 * context. Tests wrap a subtree in `<ServicesProvider services={fake}>` to
 * inject a fake; the real app passes {@link createFirebaseServices}.
 *
 * The profile directory is the ONE `creatorProfiles` subscription (public-read
 * user directory): every consumer — the account menu, avatars beside handles,
 * the /u/:handle page, the collaborator picker — derives from THIS context
 * instead of opening its own listener.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { CreatorProfile } from '@djgg/creations';
import type { PortalServices } from './types.js';
import { INITIAL_AUTH, type AuthState } from '../auth/model.js';

/** A directory row: the profile doc plus its id (the owner's uid). */
export type DirectoryProfile = CreatorProfile & { id: string };

const ServicesContext = createContext<PortalServices | null>(null);
const AuthContext = createContext<AuthState>(INITIAL_AUTH);
/** null = the first snapshot hasn't arrived yet (loading). */
const ProfileDirectoryContext = createContext<DirectoryProfile[] | null>(null);

export interface ServicesProviderProps {
  services: PortalServices;
  children: ReactNode;
  /** Optional seed auth state for tests (skips the live subscription). */
  initialAuth?: AuthState;
}

/**
 * Provide services + track auth + the profile directory. Subscribes to
 * `services.auth.onChange` and `services.subscribe.creatorProfiles` once each
 * and exposes them through {@link useAuth} / {@link useProfileDirectory}. When
 * `initialAuth` is given (tests) the subscription still runs but starts from
 * that state.
 */
export function ServicesProvider({ services, children, initialAuth }: ServicesProviderProps) {
  const [auth, setAuth] = useState<AuthState>(initialAuth ?? INITIAL_AUTH);
  const [directory, setDirectory] = useState<DirectoryProfile[] | null>(null);

  useEffect(() => {
    const unsub = services.auth.onChange((user) => {
      setAuth({ loading: false, user });
    });
    return unsub;
  }, [services]);

  useEffect(() => {
    // Public-read; a failed listener leaves the directory in loading state and
    // logs through the subscription's default warning channel.
    const unsub = services.subscribe.creatorProfiles((rows) => setDirectory(rows));
    return unsub;
  }, [services]);

  return (
    <ServicesContext.Provider value={services}>
      <AuthContext.Provider value={auth}>
        <ProfileDirectoryContext.Provider value={directory}>
          {children}
        </ProfileDirectoryContext.Provider>
      </AuthContext.Provider>
    </ServicesContext.Provider>
  );
}

/** Access the portal services. Throws if used outside a provider. */
export function useServices(): PortalServices {
  const svc = useContext(ServicesContext);
  if (!svc) throw new Error('useServices must be used within <ServicesProvider>');
  return svc;
}

/** Access the live auth state. */
export function useAuth(): AuthState {
  return useContext(AuthContext);
}

/** The live user directory. `loaded` is false until the first snapshot. */
export function useProfileDirectory(): { profiles: DirectoryProfile[]; loaded: boolean } {
  const directory = useContext(ProfileDirectoryContext);
  return { profiles: directory ?? [], loaded: directory !== null };
}

/** A single user's profile out of the directory (undefined uid → null). */
export function useProfileByUid(uid?: string): DirectoryProfile | null {
  const { profiles } = useProfileDirectory();
  if (!uid) return null;
  return profiles.find((p) => p.id === uid) ?? null;
}

/** The signed-in user's own profile. `loading` covers auth AND directory. */
export function useMyProfile(): { loading: boolean; profile: DirectoryProfile | null } {
  const { loading, user } = useAuth();
  const { profiles, loaded } = useProfileDirectory();
  if (loading || !loaded) return { loading: true, profile: null };
  if (!user || user.isAnonymous) return { loading: false, profile: null };
  return { loading: false, profile: profiles.find((p) => p.id === user.uid) ?? null };
}
